"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActivityCalendar = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_1 = __importDefault(require("@emotion/styled"));
const lodash_1 = require("lodash");
const enums_1 = require("@bemlo/enums");
const Text_1 = require("./Text");
const Tooltip_1 = require("./Tooltip");
const constants_1 = require("../constants");
const translate_1 = require("../translate");
const Grid = styled_1.default.div({
    display: 'grid',
    gridTemplateColumns: 'auto repeat(52, 1fr)',
    gridTemplateRows: 'auto repeat(7, 1fr)',
    columnGap: constants_1.theme.spacing['0.5'],
    rowGap: constants_1.theme.spacing['0.5'],
    padding: constants_1.theme.spacing['2'],
    width: 'fit-content',
});
const labelProps = {
    color: constants_1.Color.DARKEST_MEDIUM_GREY,
    fontSize: 11,
    fontWeight: constants_1.theme.font.weight.bold,
    userSelect: 'none',
};
const ColumnLabel = (0, styled_1.default)(Text_1.Text)(({ week }) => ({
    paddingLeft: week < 10 ? constants_1.theme.spacing['0.5'] : undefined,
    gridRow: 1,
    gridColumn: `${week + 1} / ${week + 5}`,
    ...labelProps,
}));
const RowLabel = (0, styled_1.default)(Text_1.Text)(({ day }) => ({
    paddingRight: constants_1.theme.spacing['1'],
    gridColumn: 1,
    gridRow: day + 1,
    ...labelProps,
}));
const Day = styled_1.default.div(({ column, row, backgroundColor }) => ({
    gridColumn: column,
    gridRow: row,
    height: constants_1.theme.spacing['2.5'],
    width: constants_1.theme.spacing['2.5'],
    backgroundColor: backgroundColor ?? constants_1.Color.MEDIUM_GREY,
    borderRadius: constants_1.theme.border.radius.small,
}));
const useWeeks = (locale) => [enums_1.Locale.SV, enums_1.Locale.DA, enums_1.Locale.FI, enums_1.Locale.NB].includes(locale);
const absoluteWeek = (yearStart, date) => {
    const dayOfYear = date.diff(yearStart, 'day') + 1;
    // The offset is the difference in days from Monday (1) if January 1st is not a Monday
    const offset = yearStart.day() === 0 ? 6 : yearStart.day() - 1;
    // Adjust the day of the year based on the offset
    const adjustedDayOfYear = dayOfYear + offset;
    // Calculate the week number
    return Math.ceil(adjustedDayOfYear / 7);
};
const ActivityCalendar = ({ year, activity }) => {
    const t = (0, translate_1.useTranslations)();
    const startDate = t.$date(year, 'YYYY');
    const shiftMap = (0, lodash_1.keyBy)(activity, 'date');
    const days = (() => {
        const days = [];
        for (let i = 0; i < 366; i++) {
            const day = startDate.add(i, 'day');
            const date = day.format('YYYY-MM-DD');
            if (date === 'Invalid Date')
                continue;
            const week = absoluteWeek(startDate, day);
            // If it is not a leap year, skip the 366th day
            if (i === 365 && day.format('YYYY') !== year)
                continue;
            const info = shiftMap[date];
            days.push((0, jsx_runtime_1.jsxs)(Tooltip_1.Tooltip, { text: info?.tooltip ? undefined : date, children: [info?.tooltip && ((0, jsx_runtime_1.jsx)(Tooltip_1.Tooltip.Content, { intent: info.tooltip.intent, children: info.tooltip.content })), (0, jsx_runtime_1.jsx)(Tooltip_1.Tooltip.Trigger, { asChild: true, children: (0, jsx_runtime_1.jsx)(Day, { column: week + 1, row: day.isoWeekday() + 1, backgroundColor: info ? info.color : undefined }) })] }, date));
        }
        return days;
    })();
    return ((0, jsx_runtime_1.jsxs)(Grid, { children: [Array.from({ length: 7 }).map((_, i) => ((0, jsx_runtime_1.jsx)(RowLabel, { day: i + 1, children: startDate.startOf('week').add(i, 'day').format('dd') }, `day-${i}`))), useWeeks(t.$locale)
                ? Array.from({ length: 18 }).map((_, i) => {
                    return ((0, jsx_runtime_1.jsx)(ColumnLabel, { week: i * 3 + 1, children: startDate.add(i * 3, 'week').format('W') }, i));
                })
                : Array.from({ length: 6 }).map((_, i) => {
                    return ((0, jsx_runtime_1.jsx)(ColumnLabel, { week: i * 9 + 1, children: startDate.add(i * 9, 'week').format('MMM') }, i));
                }), days] }));
};
exports.ActivityCalendar = ActivityCalendar;
